/**
 * Field definitions for the Zipfiles (FMO Input File Archives) grid
 * Extends base field definitions with zipfile-specific configurations
 */

import React from 'react';
import { IconButton, Typography, Box } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import { BASE_FIELDS } from '@/shared/config/baseFieldDefinitions';
import { createColumnDef, renderSummaryCell } from '@/shared/config/fieldUtils.jsx';

/**
 * Zipfile-specific fields not in base definitions
 */
const ZIPFILE_SPECIFIC_FIELDS = {
  name: {
    field: 'name',
    headerName: 'Name',
    minWidth: (theme) => theme ? parseInt(theme.spacing(20)) : 160,
    flex: 1, 
    type: 'text',
    sortable: true,
    defaultVisible: true,
    searchable: true
  },
  
  entry_count: {
    field: 'entry_count',
    headerName: 'FMO Inputs',
    width: (theme) => theme ? parseInt(theme.spacing(10)) : 80,
    type: 'number',
    sortable: true,
    defaultVisible: true,
    align: 'center',
    headerAlign: 'left'
  },
  
  actions: {
    field: 'actions',
    headerName: 'Actions',
    width: (theme) => theme ? parseInt(theme.spacing(15)) : 120,
    sortable: false,
    defaultVisible: true,
    align: 'center',
    headerAlign: 'center',
    renderCell: (params) => {
      // Don't show actions for spec rows
      if (params.row.isSpecRow) return null;

      const { handleDownload, handleEdit, theme } = params.colDef.context || {};
      return (
        <Box sx={{ display: 'flex', gap: theme?.spacing(0.5) }}>
          <IconButton
            size="small"
            onClick={(e) => handleEdit?.(params.row, e)}
            title="Rename archive"
            sx={{
              p: theme?.spacing(0.5),
              '&:hover': {
                backgroundColor: theme?.palette?.action?.hover
              }
            }}
          >
            <EditIcon sx={{ fontSize: theme?.typography?.fontSize?.lg }} />
          </IconButton>
          <IconButton
            size="small"
            onClick={(e) => handleDownload?.(params.row, e)}
            title="Download archive"
            sx={{
              p: theme?.spacing(0.5),
              '&:hover': {
                backgroundColor: theme?.palette?.action?.hover
              }
            }}
          >
            <DownloadIcon sx={{ fontSize: theme?.typography?.fontSize?.lg }} />
          </IconButton>
        </Box>
      );
    }
  },
  
  owner: {
    field: 'owner',
    headerName: 'User',
    width: (theme) => theme ? parseInt(theme.spacing(15)) : 120,
    type: 'text',
    sortable: true,
    defaultVisible: false,
    valueGetter: (_, row) => {
      // Get username from owner object for sorting
      return row.owner?.username || '';
    },
    renderCell: (params) => {
      const { getUserFullname } = params.colDef.context || {};
      const owner = params.row.owner;
      if (!owner) return '-';
      
      // Get full name using owner.id_
      const fullname = getUserFullname?.(owner.id_);
      // Fall back to username if no fullname found
      return fullname || owner.username || '-';
    }
  }
};

/**
 * Create summary field configuration for tree mode
 * Summary values are pre-calculated in tree data transformation
 */
const createSummaryField = (baseField, gridField) => ({
  ...baseField,
  field: gridField,
  valueGetter: (_, row) => {
    // Summary values are already calculated in the row data
    return row[gridField] || '';
  },
  renderCell: renderSummaryCell
});

/**
 * Get zipfile grid columns with proper order and configuration
 */
export const getZipfileColumns = (theme, context = {}) => {
  return [
    // ID column
    createColumnDef(BASE_FIELDS.id_, {}, theme),
    
    // Archive name
    createColumnDef(ZIPFILE_SPECIFIC_FIELDS.name, {}, theme),
    
    // Entry count
    createColumnDef(ZIPFILE_SPECIFIC_FIELDS.entry_count, {}, theme),
    
    // Add spec columns (Cutoff, Capping, Config) - with summaries for parent rows
    createColumnDef({
      field: 'clusterspec',
      headerName: 'Cutoff',
      width: (theme) => theme ? parseInt(theme.spacing(10)) : 80,
      type: 'text',
      sortable: true,
      defaultVisible: true,
      valueGetter: (_, row) => {
        // Summary values are pre-calculated in the row data
        return row.clusterspec || '';
      },
      renderCell: renderSummaryCell
    }, {}, theme),
    
    createColumnDef({
      field: 'pocketspec',
      headerName: 'Capping',
      width: (theme) => theme ? parseInt(theme.spacing(16)) : 80,
      type: 'text',
      sortable: true,
      defaultVisible: true,
      valueGetter: (_, row) => {
        // Summary values are pre-calculated in the row data
        return row.pocketspec || '';
      },
      renderCell: renderSummaryCell
    }, {}, theme),
    
    createColumnDef({
      field: 'inputspec',
      headerName: 'Config',
      width: (theme) => theme ? parseInt(theme.spacing(14)) : 80,
      type: 'text',
      sortable: true,
      defaultVisible: true,
      valueGetter: (_, row) => {
        // Summary values are pre-calculated in the row data
        return row.inputspec || '';
      },
      renderCell: renderSummaryCell
    }, {}, theme),
    
    // Summary fields for tree data
    createColumnDef(
      createSummaryField(BASE_FIELDS.project, 'project'),
      {}, 
      theme
    ),
    
    createColumnDef(
      createSummaryField(BASE_FIELDS.target, 'target'),
      {}, 
      theme
    ),
    
    createColumnDef(
      createSummaryField(BASE_FIELDS.mutation, 'mutation'),
      { defaultVisible: false }, 
      theme
    ),
    
    createColumnDef(
      createSummaryField(BASE_FIELDS.batch, 'batch'),
      {}, 
      theme
    ),
    
    createColumnDef(
      createSummaryField(BASE_FIELDS.ligand_name, 'ligandName'),
      { defaultVisible: false },  // Hidden by default in zipfiles
      theme
    ),
    
    createColumnDef(
      createSummaryField(BASE_FIELDS.ligand_tag, 'ligandTag'),
      { defaultVisible: false },  // Hidden by default in zipfiles
      theme
    ),
    
    createColumnDef(
      createSummaryField(BASE_FIELDS.author, 'author'),
      { defaultVisible: false }, 
      theme
    ),
    
    // Dates
    createColumnDef(BASE_FIELDS.created_at, {
      valueGetter: (value) => {
        if (!value) return null;
        return new Date(value); // Return Date object for dateTime column type
      },
      renderCell: (params) => {
        if (!params.value) return '-';
        return params.value.toLocaleString();
      }
    }, theme),
    
    // User
    createColumnDef(ZIPFILE_SPECIFIC_FIELDS.owner, { context }, theme),
    
    // Actions (download button)
    createColumnDef(ZIPFILE_SPECIFIC_FIELDS.actions, { context }, theme)
  ];
};

/**
 * Get default column visibility for zipfiles grid
 */
export const getZipfileDefaultVisibility = (columns) => {
  return columns.reduce((acc, col) => {
    if (col.defaultVisible === false) {
      acc[col.field] = false;
    }
    return acc;
  }, {});
};